import { useId } from 'react';
import { ACADEMY_NAME } from '@/shared/wordmark';

/**
 * The Horizon mark (Brendon's choice of 22 September 2026; master artwork and rules in the project
 * folder's `assets/brand/`, copied beside `scripts/brand/render-icons.sh`): a disc meeting the
 * water, its reflection drawn as four rules that thin toward the bottom. The geometry is the
 * master's, on its 64-unit grid, never redrawn: a circle of radius 28 at 32,32 clips an upper
 * block from y 0 to 30 and bands of 5, 4, 3 and 2.2 units at y 33.5, 42, 49.5 and 56.
 *
 * One colour, `--mark`: the brand blue on light grounds and the lifted blue on dark. It is the
 * Academy's own mark, so it never takes an operator's accent. `size` is the box in pixels; `null`
 * leaves the box to the stylesheet, as the wordmark lockup does.
 */
export function HorizonMark({ size = 28, className = '' }: { size?: number | null; className?: string }) {
  // One page can draw the mark several times (rail, footer, brand preview), so each clip gets its
  // own id; the characters React adds to `useId` are dropped so the reference stays a plain URL.
  const clip = `hz${useId().replace(/[^A-Za-z0-9_-]/g, '')}`;
  return (
    <span
      className={`mk ${className}`}
      style={size === null ? undefined : { width: size, height: size }}
      aria-hidden="true"
      data-mark="horizon"
    >
      <svg viewBox="0 0 64 64">
        <defs>
          <clipPath id={clip}>
            <circle cx="32" cy="32" r="28" />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clip})`} fill="var(--mark)">
          <rect x="0" y="0" width="64" height="30" />
          <rect x="0" y="33.5" width="64" height="5" />
          <rect x="0" y="42" width="64" height="4" />
          <rect x="0" y="49.5" width="64" height="3" />
          <rect x="0" y="56" width="64" height="2.2" />
        </g>
      </svg>
    </span>
  );
}

/**
 * The Expedition Certified lockup: the Horizon mark beside the Crimson Pro wordmark, scaled as one
 * unit from `size`, the mark's box in pixels. Without `size` the stylesheet sets it (the header's
 * responsive sizes). Only the Academy's own branding uses it; see `usesAcademyWordmark`.
 */
export function AcademyLockup({ size, className = '' }: { size?: number; className?: string }) {
  return (
    <span
      className={`wordmark ${className}`}
      style={size === undefined ? undefined : { ['--wm' as string]: `${size}px` }}
      data-wordmark="academy"
    >
      <HorizonMark size={null} />
      <span className="wordmark-text">{ACADEMY_NAME}</span>
    </span>
  );
}

type MarkProps = {
  name: string;
  logoUrl: string | null;
  brandMode: 'endorsed' | 'whitelabel';
  size?: number;
  className?: string;
};

/**
 * Use the tenant's logo, with the Academy's Horizon mark as the fallback only for endorsed
 * academies. White label shows no Academy mark anywhere.
 */
export function Mark({ name, logoUrl, brandMode, size = 28, className = '' }: MarkProps) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={`${name} logo`}
        className={`block shrink-0 object-contain ${className}`}
        style={{ height: size, maxWidth: size * 4 }}
      />
    );
  }
  return brandMode === 'endorsed' ? <HorizonMark size={size} className={className} /> : null;
}
